import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../config/const";
import { useNavigate } from "react-router-dom";
import "./PersonaPicker.css";

// Group children by first name: "Shreya", "Shreya singh", "Shreya new2" → one persona
const groupChildrenByName = (children) => {
  const getBaseName = (name) =>
    name.trim().split(/\s+/)[0].toLowerCase();

  const groups = {};
  children.forEach((child) => {
    const base = getBaseName(child.name);
    if (!groups[base]) {
      groups[base] = {
        // Use the first child as the "primary" record
        _id: child._id,
        _ids: [child._id],
        name: child.name.trim().split(/\s+/)[0], // use first name as display name
        dateOfBirth: child.dateOfBirth,
        profilePicture: child.profilePicture || null,
        cartoonImage: [...(child.cartoonImage || [])],
        cartoonImageId: [...(child.cartoonImageId || [])],
      };
    } else {
      const g = groups[base];
      g._ids.push(child._id);
      // Use profile picture from whichever has one
      if (!g.profilePicture && child.profilePicture) {
        g.profilePicture = child.profilePicture;
      }
      // Merge cartoon images
      if (child.cartoonImage) {
        g.cartoonImage.push(...child.cartoonImage);
      }
      if (child.cartoonImageId) {
        g.cartoonImageId.push(...child.cartoonImageId);
      }
      // Use the earliest DOB (youngest age wins for display)
      if (child.dateOfBirth && new Date(child.dateOfBirth) > new Date(g.dateOfBirth)) {
        g.dateOfBirth = child.dateOfBirth;
      }
    }
  });
  return Object.values(groups);
};

const DAILY_STORY_LIMIT = 2;

const PersonaPicker = () => {
  const [children, setChildren] = useState([]);
  const [groupedChildren, setGroupedChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todayStoryCount, setTodayStoryCount] = useState(0);
  const rawEmail = localStorage.getItem("email");
  const email = (!rawEmail || rawEmail === "null" || rawEmail === "undefined") ? null : rawEmail;
  const navigate = useNavigate();

  // Guard: redirect to login immediately if not authenticated
  useEffect(() => {
    if (!email) {
      navigate("/login");
    }
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!email) return; // don't fetch if unauthenticated
    const fetchData = async () => {
      // Fetch children and stories independently so one failure doesn't block the other
      try {
        const childrenRes = await axios.get(`${config.backend_url}/user/children`, { params: { email } });
        const raw = childrenRes.data.children || [];
        setChildren(raw);
        setGroupedChildren(groupChildrenByName(raw));
      } catch (error) {
        console.error("Error fetching children:", error);
      }

      try {
        const storiesRes = await axios.get(`${config.backend_url}/user/getStories?email=${email}`);
        const allStories = storiesRes.data.stories || [];
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const todayCount = allStories.filter(
          (s) => new Date(s.createdAt) >= startOfToday
        ).length;
        setTodayStoryCount(todayCount);
      } catch (error) {
        console.error("Error fetching stories:", error);
      }

      setLoading(false);
    };
    fetchData();
  }, [email]);

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const dob = new Date(dateOfBirth);
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const monthDiff = now.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  const handleSelectChild = (persona) => {
    // Navigate to story form with merged persona data
    navigate("/create/story", {
      state: {
        selectedChild: {
          _id: persona._id,
          _ids: persona._ids, // all child IDs in this group
          name: persona.name,
          age: calculateAge(persona.dateOfBirth),
          dateOfBirth: persona.dateOfBirth,
          profilePicture: persona.profilePicture,
          cartoonImage: persona.cartoonImage,
          cartoonImageId: persona.cartoonImageId,
        },
      },
    });
  };

  const handleSkip = () => {
    // Go to StoryForm without a selected child (legacy flow)
    navigate("/create/story");
  };

  if (loading) {
    return (
      <div className="persona-picker">
        <div className="persona-loading">
          <div className="persona-spinner"></div>
          <p>Loading characters...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="persona-picker">
      <div className="persona-header">
        <h1>Who is this story for?</h1>
        <p className="persona-subtitle">
          Pick a character to get personalized story suggestions
        </p>
      </div>

      {todayStoryCount >= DAILY_STORY_LIMIT ? (
        <div className="daily-limit-card">
          <div className="daily-limit-icon">📚</div>
          <h2>You've created {todayStoryCount} stories today!</h2>
          <p>Come back tomorrow for more adventures.</p>
          <p className="daily-limit-sub">You can create up to {DAILY_STORY_LIMIT} stories per day.</p>
          <div className="daily-limit-actions">
            <button className="daily-limit-btn" onClick={() => navigate('/my-stories')}>
              View My Stories
            </button>
            <button className="daily-limit-btn secondary" onClick={() => navigate('/knowledge')}>
              Explore Learning Map
            </button>
          </div>
        </div>
      ) : (
        <>
        {todayStoryCount > 0 && (
          <div className="daily-limit-info">
            {DAILY_STORY_LIMIT - todayStoryCount} of {DAILY_STORY_LIMIT} stories remaining today
          </div>
        )}

      <div className="persona-grid">
        {groupedChildren.map((persona) => {
          const age = calculateAge(persona.dateOfBirth);
          const avatar =
            (persona.cartoonImage && persona.cartoonImage[0]) ||
            persona.profilePicture ||
            null;

          return (
            <div
              key={persona._id}
              className="persona-card"
              onClick={() => handleSelectChild(persona)}
            >
              <div className="persona-avatar">
                {avatar ? (
                  <img
                    src={avatar}
                    alt={persona.name}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling && (e.target.nextSibling.style.display = "flex");
                    }}
                  />
                ) : null}
                <div
                  className="persona-avatar-placeholder"
                  style={{ display: avatar ? "none" : "flex" }}
                >
                  {persona.name.charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="persona-info">
                <h3>{persona.name}</h3>
                {age !== null && (
                  <span className="persona-age">
                    {age} year{age !== 1 ? "s" : ""} old
                  </span>
                )}
              </div>
              {persona.cartoonImage && persona.cartoonImage.length > 0 && (
                <div className="persona-styles-preview">
                  {persona.cartoonImage.slice(0, 6).map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`${persona.name} style ${idx + 1}`}
                      className="persona-style-thumb"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ))}
                </div>
              )}
              <button
                className="persona-learning-map-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/knowledge', {
                    state: {
                      selectedChild: {
                        _id: persona._id,
                        _ids: persona._ids,
                        name: persona.name,
                        age: calculateAge(persona.dateOfBirth),
                        dateOfBirth: persona.dateOfBirth,
                        profilePicture: persona.profilePicture,
                        cartoonImage: persona.cartoonImage,
                        cartoonImageId: persona.cartoonImageId,
                      },
                    },
                  });
                }}
              >
                Learning Map
              </button>
              <div className="persona-card-arrow">→</div>
            </div>
          );
        })}

        {/* Add new child card */}
        <div
          className="persona-card persona-card-add"
          onClick={() =>
            navigate("/profile", {
              state: {
                openForm: true,
                openChildForm: true,
                scrollTo: "cartoonify-feature-intro",
                fromStory: true,
                returnTo: "/create",
              },
            })
          }
        >
          <div className="persona-avatar">
            <div className="persona-avatar-placeholder persona-add-icon">+</div>
          </div>
          <div className="persona-info">
            <h3>Add a child</h3>
            <span className="persona-age">Create a new character</span>
          </div>
        </div>
      </div>

      <button className="persona-skip" onClick={handleSkip}>
        Skip — write a story without a character
      </button>
      </>
      )}
    </div>
  );
};

export default PersonaPicker;
